import React from 'react';
import type { BadgeTone } from '../../domain/property/badges';

/**
 * Badge — v2(Stitch) 공통 배지. docs/design-v2/stitch-ui.css .badge / .b-* / .tag / .tagpill 값 그대로.
 *
 * tone: o(연오렌지) · osolid(오렌지 솔리드) · ylw(노랑) · blue · green · red · mute · violet
 * size: md(.badge 11px/700) · tag(.badge.tag 10px/800)
 * shape: rect(radius 6) · pill(999 — 히어로 오버레이 .tagpill)
 *
 * 노출 규칙(어떤 배지를 띄울지)은 domain/property/badges.ts buildBadges 가 결정한다.
 */

export type BadgeSize = 'md' | 'tag';
export type BadgeShape = 'rect' | 'pill';

export interface BadgeProps {
  tone: BadgeTone;
  size?: BadgeSize;
  shape?: BadgeShape;
  ariaLabel?: string;
  className?: string;
  children: React.ReactNode;
}

const toneClasses: Record<BadgeTone, string> = {
  o: 'bg-accent-50 text-accent',
  osolid: 'bg-accent text-white',
  ylw: 'bg-warn-bg text-warn',
  blue: 'bg-info-bg text-info',
  green: 'bg-ok-bg text-[#047857]',
  red: 'bg-bad-bg text-[#B91C1C]',
  mute: 'bg-ca-surface text-[#475569]',
  violet: 'bg-violet-soft text-violet',
};

const sizeClasses: Record<BadgeSize, string> = {
  md: 'text-[11px] leading-[14px] font-bold',
  tag: 'text-[10px] leading-[12px] font-extrabold',
};

export const Badge: React.FC<BadgeProps> = ({
  tone,
  size = 'md',
  shape = 'rect',
  ariaLabel,
  className = '',
  children,
}) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap ${
      shape === 'pill' ? 'rounded-full px-2 py-[3px]' : 'rounded-ca-sm px-2 py-1'
    } ${sizeClasses[size]} ${toneClasses[tone]} ${className}`}
    title={ariaLabel}
    data-badge-tone={tone}
  >
    {children}
  </span>
);

export default Badge;
